import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Ban,
	CircleDollarSign,
	ChevronDown,
	Key,
	Loader2,
	Pencil,
	Plus,
	Search,
	ShieldCheck,
	Trash2,
	UserRound,
	X,
} from "lucide-react";
import { useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { BrandSelect } from "@/components/brand/brand-select";
import { Header } from "@/components/Header";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSession } from "@/hooks/auth-hooks";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/admin/users")({
	component: AdminUsersPage,
});

type ListedUser = {
	id: string;
	email?: string | null;
	name?: string | null;
	role?: string | null;
	banned?: boolean | null;
	banReason?: string | null;
	banExpires?: number | null;
};

type UserSession = {
	id: string;
	token: string;
	userId: string;
	expiresAt: Date;
	createdAt: Date;
	updatedAt: Date;
	ipAddress?: string | null;
	userAgent?: string | null;
};


type DialogState = {
	showCreate: boolean;
	userToGrantBalance: ListedUser | null;
	userToEdit: ListedUser | null;
	userToDelete: ListedUser | null;
	userToBan: ListedUser | null;
	sessionsUser: ListedUser | null;
};

type DialogAction =
	| { type: "OPEN_CREATE" }
	| { type: "CLOSE_CREATE" }
	| { type: "SET_GRANT_BALANCE"; user: ListedUser | null }
	| { type: "SET_EDIT"; user: ListedUser | null }
	| { type: "SET_DELETE"; user: ListedUser | null }
	| { type: "SET_BAN"; user: ListedUser | null }
	| { type: "SET_SESSIONS"; user: ListedUser | null };

const dialogInitialState: DialogState = {
	showCreate: false,
	userToGrantBalance: null,
	userToEdit: null,
	userToDelete: null,
	userToBan: null,
	sessionsUser: null,
};

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
	switch (action.type) {
		case "OPEN_CREATE":
			return { ...state, showCreate: true };
		case "CLOSE_CREATE":
			return { ...state, showCreate: false };
		case "SET_GRANT_BALANCE":
			return { ...state, userToGrantBalance: action.user };
		case "SET_EDIT":
			return { ...state, userToEdit: action.user };
		case "SET_DELETE":
			return { ...state, userToDelete: action.user };
		case "SET_BAN":
			return { ...state, userToBan: action.user };
		case "SET_SESSIONS":
			return { ...state, sessionsUser: action.user };
	}
}

function AdminUsersPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const [users, setUsers] = useState<ListedUser[]>([]);
	const [search, setSearch] = useState("");
	const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(
		null,
	);
	const [dialog, dispatch] = useReducer(dialogReducer, dialogInitialState);
	const [deleting, setDeleting] = useState(false);

	// Create user state
	const [createForm, setCreateForm] = useState({
		email: "",
		password: "",
		name: "",
		role: "user",
	});
	const [creating, setCreating] = useState(false);

	// Edit user state
	const [editForm, setEditForm] = useState({ name: "", role: "" });
	const [updating, setUpdating] = useState(false);

	// Ban user state
	const [banForm, setBanForm] = useState({ reason: "", expiresInDays: "" });
	const [banningUserId, setBanningUserId] = useState<string | null>(null);

	// Sessions state
	const [sessions, setSessions] = useState<UserSession[]>([]);
	const [loadingSessions, setLoadingSessions] = useState(false);
	const [revokingSessionToken, setRevokingSessionToken] = useState<
		string | null
	>(null);
	const [revokingAllSessions, setRevokingAllSessions] = useState(false);

	const [grantBalanceForm, setGrantBalanceForm] = useState({
		amount: "",
		note: "",
		workspaceId: "",
	});

	const userWorkspacesQuery = useQuery({
		enabled: !!dialog.userToGrantBalance,
		queryKey: ["admin", "user-workspaces", dialog.userToGrantBalance?.id],
		queryFn: async () => {
			const res = await fetch(
				`/api/admin/user-workspaces?userId=${dialog.userToGrantBalance!.id}`,
				{ credentials: "include" },
			);
			if (!res.ok) throw new Error("Failed to fetch workspaces");
			return res.json() as Promise<{
				workspaces: { id: string; name: string; slug: string }[];
			}>;
		},
	});
	const [grantingBalanceUserId, setGrantingBalanceUserId] = useState<string | null>(
		null,
	);
	const [balances, setBalances] = useState<Record<string, number>>({});

	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({ to: "/login", replace: true });
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { isLoading: loadingUsers } = useQuery({
		queryKey: ["admin-users", user?.id],
		queryFn: async () => {
			const usersResult = await authClient.admin.listUsers({
				query: { limit: 200 },
			});

			if (usersResult.error) {
				throw new Error(usersResult.error.message || "Failed to load users");
			}

			setUsers((usersResult.data?.users as ListedUser[]) ?? []);
			return true;
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	useQuery({
		queryKey: ["admin-user-balances", users.map((entry) => entry.id).join(",")],
		queryFn: async () => {
			if (users.length === 0) return true;
			const params = new URLSearchParams({
				ids: users.map((entry) => entry.id).join(","),
			});
			const res = await fetch(`/api/admin/user-balances?${params.toString()}`, {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load balances");
			const data = await res.json();
			setBalances(data.balances ?? {});
			return true;
		},
		enabled: !isPending && !!session && !!user && isAdmin && users.length > 0,
	});

	const handleGrantBalance = async () => {
		if (!dialog.userToGrantBalance) return;
		const amountDollars = Number(grantBalanceForm.amount);
		const amountCents = Number.isFinite(amountDollars)
			? Math.round(amountDollars * 100)
			: 0;

		if (amountCents <= 0) {
			toast.error("Enter a valid dollar amount");
			return;
		}

		const workspaces = userWorkspacesQuery.data?.workspaces ?? [];
		const workspaceId = grantBalanceForm.workspaceId || workspaces[0]?.id;
		if (!workspaceId) {
			toast.error("This user has no workspaces");
			return;
		}

		setGrantingBalanceUserId(dialog.userToGrantBalance.id);
		try {
			const res = await fetch("/api/admin/grant-balance", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					amountCents,
					note: grantBalanceForm.note.trim() || undefined,
					userId: dialog.userToGrantBalance.id,
					workspaceId,
				}),
			});
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data.error || "Failed to grant balance");
			}

			setBalances((prev) => ({
				...prev,
				[dialog.userToGrantBalance!.id]: data.balanceCents,
			}));
			toast.success("Balance granted");
			dispatch({ type: "SET_GRANT_BALANCE", user: null });
			setGrantBalanceForm({ amount: "", note: "", workspaceId: "" });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to grant balance",
			);
		} finally {
			setGrantingBalanceUserId(null);
		}
	};

	const filteredUsers = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return users;
		return users.filter((u) => {
			const email = (u.email ?? "").toLowerCase();
			const name = (u.name ?? "").toLowerCase();
			return (
				email.includes(query) || name.includes(query) || u.id.includes(query)
			);
		});
	}, [users, search]);

	const handleImpersonate = async (targetUserId: string) => {
		if (!targetUserId || targetUserId === user?.id) return;
		setImpersonatingUserId(targetUserId);
		try {
			const result = await authClient.admin.impersonateUser({
				userId: targetUserId,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to impersonate user");
			}

			window.location.href = "/dashboard";
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to impersonate user",
			);
			setImpersonatingUserId(null);
		}
	};

	const handleDeleteUser = async () => {
		if (!dialog.userToDelete) return;
		setDeleting(true);
		try {
			const result = await authClient.admin.removeUser({
				userId: dialog.userToDelete.id,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to delete user");
			}

			const userToDeleteId = dialog.userToDelete.id;
			setUsers((prev) => prev.filter((u) => u.id !== userToDeleteId));
			dispatch({ type: "SET_DELETE", user: null });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to delete user",
			);
		} finally {
			setDeleting(false);
		}
	};

	// Create user handler
	const handleCreateUser = async () => {
		if (!createForm.email || !createForm.password) {
			toast.error("Email and password are required");
			return;
		}
		setCreating(true);
		try {
			const result = await authClient.admin.createUser({
				email: createForm.email,
				password: createForm.password,
				name: createForm.name || "",
				role: (createForm.role || "user") as "user" | "admin",
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to create user");
			}

			if (result.data?.user) {
				setUsers((prev) => [result.data.user as ListedUser, ...prev]);
			}

			dispatch({ type: "CLOSE_CREATE" });
			setCreateForm({ email: "", password: "", name: "", role: "user" });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to create user",
			);
		} finally {
			setCreating(false);
		}
	};

	// Edit user handler
	const handleOpenEditDialog = (u: ListedUser) => {
		dispatch({ type: "SET_EDIT", user: u });
		setEditForm({ name: u.name || "", role: u.role || "user" });
	};

	const handleUpdateUser = async () => {
		if (!dialog.userToEdit) return;
		setUpdating(true);
		try {
			if (editForm.role !== (dialog.userToEdit.role || "user")) {
				const roleResult = await authClient.admin.setRole({
					userId: dialog.userToEdit.id,
					role: editForm.role as "user" | "admin",
				});
				if (roleResult.error) {
					throw new Error(roleResult.error.message || "Failed to update role");
				}
			}

			const userToEditId = dialog.userToEdit.id;
			setUsers((prev) =>
				prev.map((u) =>
					u.id === userToEditId
						? { ...u, name: editForm.name, role: editForm.role }
						: u,
				),
			);

			dispatch({ type: "SET_EDIT", user: null });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to update user",
			);
		} finally {
			setUpdating(false);
		}
	};

	// Ban user handler
	const handleBanUser = async () => {
		if (!dialog.userToBan) return;
		setBanningUserId(dialog.userToBan.id);
		try {
			const expiresInSeconds = banForm.expiresInDays
				? Number.parseInt(banForm.expiresInDays, 10) * 24 * 60 * 60
				: undefined;

			const result = await authClient.admin.banUser({
				userId: dialog.userToBan.id,
				banReason: banForm.reason || undefined,
				banExpiresIn: expiresInSeconds,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to ban user");
			}

			const userToBanId = dialog.userToBan.id;
			setUsers((prev) =>
				prev.map((u) =>
					u.id === userToBanId
						? {
								...u,
								banned: true,
								banReason: banForm.reason || null,
								banExpires: expiresInSeconds
									? Date.now() + expiresInSeconds * 1000
									: null,
							}
						: u,
				),
			);

			dispatch({ type: "SET_BAN", user: null });
			setBanForm({ reason: "", expiresInDays: "" });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to ban user",
			);
		} finally {
			setBanningUserId(null);
		}
	};

	// Unban user handler
	const handleUnbanUser = async (targetUserId: string) => {
		setBanningUserId(targetUserId);
		try {
			const result = await authClient.admin.unbanUser({
				userId: targetUserId,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to unban user");
			}

			setUsers((prev) =>
				prev.map((u) =>
					u.id === targetUserId
						? { ...u, banned: false, banReason: null, banExpires: null }
						: u,
				),
			);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to unban user",
			);
		} finally {
			setBanningUserId(null);
		}
	};

	// Load sessions handler
	const handleLoadSessions = async (targetUser: ListedUser) => {
		dispatch({ type: "SET_SESSIONS", user: targetUser });
		setLoadingSessions(true);
		try {
			const result = await authClient.admin.listUserSessions({
				userId: targetUser.id,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to load sessions");
			}

			setSessions((result.data?.sessions as UserSession[]) ?? []);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to load sessions",
			);
		} finally {
			setLoadingSessions(false);
		}
	};

	// Revoke session handler
	const handleRevokeSession = async (sessionToken: string) => {
		setRevokingSessionToken(sessionToken);
		try {
			const result = await authClient.admin.revokeUserSession({
				sessionToken,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to revoke session");
			}

			setSessions((prev) => prev.filter((s) => s.token !== sessionToken));
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to revoke session",
			);
		} finally {
			setRevokingSessionToken(null);
		}
	};

	// Revoke all sessions handler
	const handleRevokeAllSessions = async () => {
		if (!dialog.sessionsUser) return;
		setRevokingAllSessions(true);
		try {
			const result = await authClient.admin.revokeUserSessions({
				userId: dialog.sessionsUser.id,
			});

			if (result.error) {
				throw new Error(result.error.message || "Failed to revoke sessions");
			}

			setSessions([]);
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to revoke all sessions",
			);
		} finally {
			setRevokingAllSessions(false);
		}
	};

	if (isPending || loadingUsers || !isAdmin) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<Header />

			<main className="max-w-6xl mx-auto px-4 py-8">
				<div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<h1 className="text-3xl font-semibold text-foreground">Users</h1>
						<p className="mt-1 text-sm text-muted-foreground">
							Manage all registered users
						</p>
					</div>

					<div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
						<div className="relative w-full sm:w-80">
							<Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
							<input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								aria-label="Search users by name, email, or ID"
								placeholder="Search by name, email, or ID..."
								className="h-9 w-full border border-border0 bg-background pl-9 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
							/>
						</div>
						<BrandButton
							className="h-9 text-sm"
							onClick={() => dispatch({ type: "OPEN_CREATE" })}
						>
							<Plus className="w-4 h-4 mr-1" />
							Create User
						</BrandButton>
					</div>
				</div>

				<BrandCard>
					<BrandCardHeader>
						<BrandCardTitle className="flex items-center gap-2">
							<UserRound className="w-5 h-5" />
							All Users
						</BrandCardTitle>
						<BrandCardDescription className="text-sm leading-6">
							{filteredUsers.length} user
							{filteredUsers.length !== 1 ? "s" : ""} found
						</BrandCardDescription>
					</BrandCardHeader>
					<BrandCardContent>
						<div className="divide-y divide-border">
							{filteredUsers.map((u) => (
								<div
									key={u.id}
									className="py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
								>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="truncate text-sm font-bold">
												{u.email || u.name || u.id}
											</span>
											{u.banned && (
												<span className="inline-flex items-center bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
													<Ban className="w-3 h-3 mr-1" />
													Banned
												</span>
											)}
											{u.role === "admin" && (
												<span className="inline-flex items-center bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
													<ShieldCheck className="w-3 h-3 mr-1" />
													Admin
												</span>
											)}
													</div>
										<div className="truncate text-xs text-muted-foreground">
											{u.id}
											{u.name && u.email ? ` \u2022 ${u.name}` : ""}
											{u.banReason ? ` \u2022 Ban reason: ${u.banReason}` : ""}
										</div>
										<div className="mt-1 text-xs font-bold text-foreground/80">
											Balance:{" "}
											{new Intl.NumberFormat("en-US", {
												style: "currency",
												currency: "USD",
											}).format((balances[u.id] ?? 0) / 100)}
										</div>
									</div>

									<div className="flex items-center gap-2 flex-wrap">
										<BrandButton
											size="sm"
											className="h-9 text-sm"
											onClick={() => handleImpersonate(u.id)}
											disabled={
												u.id === user?.id ||
												impersonatingUserId === u.id ||
												u.banned === true
											}
										>
											{impersonatingUserId === u.id ? (
												<Loader2 className="w-4 h-4 animate-spin" />
											) : (
												"Impersonate"
											)}
										</BrandButton>

										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<BrandButton
													size="sm"
													variant="secondary"
													className="h-9 text-sm"
												>
													Actions
													<ChevronDown className="w-4 h-4 ml-1" />
												</BrandButton>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="w-48">
												<DropdownMenuItem
													onClick={() => handleOpenEditDialog(u)}
												>
													<Pencil className="w-4 h-4 mr-2" />
													Edit User
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => handleLoadSessions(u)}>
													<Key className="w-4 h-4 mr-2" />
													Sessions
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												{u.banned ? (
													<DropdownMenuItem
														onClick={() => handleUnbanUser(u.id)}
														disabled={banningUserId === u.id}
													>
														{banningUserId === u.id ? (
															<Loader2 className="w-4 h-4 mr-2 animate-spin" />
														) : (
															<ShieldCheck className="w-4 h-4 mr-2" />
														)}
														Unban User
													</DropdownMenuItem>
												) : (
													<DropdownMenuItem
														onClick={() =>
															dispatch({ type: "SET_BAN", user: u })
														}
														disabled={u.id === user?.id}
													>
														<Ban className="w-4 h-4 mr-2" />
														Ban User
													</DropdownMenuItem>
												)}
												<DropdownMenuSeparator />
												<DropdownMenuItem
													onClick={() => {
														dispatch({ type: "SET_GRANT_BALANCE", user: u });
														setGrantBalanceForm({ amount: "", note: "", workspaceId: "" });
													}}
												>
													<CircleDollarSign className="w-4 h-4 mr-2" />
													Grant Balance
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem
													onClick={() =>
														dispatch({ type: "SET_DELETE", user: u })
													}
													disabled={u.id === user?.id}
													className="text-red-600 focus:text-red-600"
												>
													<Trash2 className="w-4 h-4 mr-2" />
													Delete User
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</div>
							))}

							{filteredUsers.length === 0 && (
								<div className="py-10 text-center text-sm text-muted-foreground">
									No users found
								</div>
							)}
						</div>
					</BrandCardContent>
				</BrandCard>
			</main>

			{/* Delete user dialog */}
			<AlertDialog
				open={!!dialog.userToDelete}
				onOpenChange={() => dispatch({ type: "SET_DELETE", user: null })}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete User</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-bold">
								{dialog.userToDelete?.email ||
									dialog.userToDelete?.name ||
									dialog.userToDelete?.id}
							</span>
							? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteUser}
							disabled={deleting}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{deleting ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Create user dialog */}
			<Dialog
				open={dialog.showCreate}
				onOpenChange={(open) =>
					dispatch({ type: open ? "OPEN_CREATE" : "CLOSE_CREATE" })
				}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Create User</DialogTitle>
						<DialogDescription>
							Create a new user account manually.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<BrandInputGroup
							label="Email"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="email"
								className="h-9 text-sm file:text-sm"
								placeholder="user@example.com"
								value={createForm.email}
								onChange={(e) =>
									setCreateForm((prev) => ({
										...prev,
										email: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Password"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="password"
								className="h-9 text-sm file:text-sm"
								placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
								value={createForm.password}
								onChange={(e) =>
									setCreateForm((prev) => ({
										...prev,
										password: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Name (optional)"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="text"
								className="h-9 text-sm file:text-sm"
								placeholder="John Doe"
								value={createForm.name}
								onChange={(e) =>
									setCreateForm((prev) => ({
										...prev,
										name: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Role"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandSelect
								className="h-9 text-sm"
								value={createForm.role}
								onChange={(e) =>
									setCreateForm((prev) => ({
										...prev,
										role: e.target.value,
									}))
								}
								options={[
									{ value: "user", label: "User" },
									{ value: "admin", label: "Admin" },
								]}
							/>
						</BrandInputGroup>
					</div>
					<DialogFooter>
						<BrandButton
							variant="secondary"
							className="h-9 text-sm"
							onClick={() => dispatch({ type: "CLOSE_CREATE" })}
						>
							Cancel
						</BrandButton>
						<BrandButton
							className="h-9 text-sm"
							onClick={handleCreateUser}
							disabled={creating}
						>
							{creating ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							Create
						</BrandButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit user dialog */}
			<Dialog
				open={!!dialog.userToEdit}
				onOpenChange={() => dispatch({ type: "SET_EDIT", user: null })}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit User</DialogTitle>
						<DialogDescription>
							Update details for{" "}
							{dialog.userToEdit?.email ||
								dialog.userToEdit?.name ||
								dialog.userToEdit?.id}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<BrandInputGroup
							label="Name"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="text"
								className="h-9 text-sm file:text-sm"
								placeholder="John Doe"
								value={editForm.name}
								onChange={(e) =>
									setEditForm((prev) => ({
										...prev,
										name: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Role"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandSelect
								className="h-9 text-sm"
								value={editForm.role}
								onChange={(e) =>
									setEditForm((prev) => ({
										...prev,
										role: e.target.value,
									}))
								}
								options={[
									{ value: "user", label: "User" },
									{ value: "admin", label: "Admin" },
								]}
							/>
						</BrandInputGroup>
					</div>
					<DialogFooter>
						<BrandButton
							variant="secondary"
							className="h-9 text-sm"
							onClick={() => dispatch({ type: "SET_EDIT", user: null })}
						>
							Cancel
						</BrandButton>
						<BrandButton
							className="h-9 text-sm"
							onClick={handleUpdateUser}
							disabled={updating}
						>
							{updating ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							Save
						</BrandButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={!!dialog.userToGrantBalance}
				onOpenChange={() => dispatch({ type: "SET_GRANT_BALANCE", user: null })}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Grant Balance</DialogTitle>
						<DialogDescription>
							Add dollar balance to{" "}
							{dialog.userToGrantBalance?.email ||
								dialog.userToGrantBalance?.name ||
								dialog.userToGrantBalance?.id}
							.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<BrandInputGroup
							label="Workspace"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							{userWorkspacesQuery.isLoading ? (
								<div className="h-9 flex items-center text-sm text-muted-foreground">
									<Loader2 className="w-3 h-3 animate-spin mr-2" />
									Loading workspaces…
								</div>
							) : (
								<BrandSelect
									className="h-9 text-sm"
									options={
										(userWorkspacesQuery.data?.workspaces ?? []).length === 0
											? [{ value: "", label: "No workspaces" }]
											: (userWorkspacesQuery.data?.workspaces ?? []).map(
													(ws) => ({ value: ws.id, label: ws.name }),
												)
									}
									value={
										grantBalanceForm.workspaceId ||
										(userWorkspacesQuery.data?.workspaces[0]?.id ?? "")
									}
									onChange={(e) =>
										setGrantBalanceForm((prev) => ({
											...prev,
											workspaceId: e.target.value,
										}))
									}
								/>
							)}
						</BrandInputGroup>
						<BrandInputGroup
							label="Amount (USD)"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="number"
								min="0.01"
								step="0.01"
								className="h-9 text-sm file:text-sm"
								placeholder="25.00"
								value={grantBalanceForm.amount}
								onChange={(e) =>
									setGrantBalanceForm((prev) => ({
										...prev,
										amount: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Note (optional)"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="text"
								className="h-9 text-sm file:text-sm"
								placeholder="Manual credit"
								value={grantBalanceForm.note}
								onChange={(e) =>
									setGrantBalanceForm((prev) => ({
										...prev,
										note: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
					</div>
					<DialogFooter>
						<BrandButton
							variant="secondary"
							className="h-9 text-sm"
							onClick={() => dispatch({ type: "SET_GRANT_BALANCE", user: null })}
						>
							Cancel
						</BrandButton>
						<BrandButton
							className="h-9 text-sm"
							onClick={handleGrantBalance}
							disabled={grantingBalanceUserId === dialog.userToGrantBalance?.id}
						>
							{grantingBalanceUserId === dialog.userToGrantBalance?.id ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : null}
							Grant balance
						</BrandButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Ban user dialog */}
			<Dialog
				open={!!dialog.userToBan}
				onOpenChange={() => dispatch({ type: "SET_BAN", user: null })}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Ban User</DialogTitle>
						<DialogDescription>
							Ban{" "}
							<span className="font-bold">
								{dialog.userToBan?.email ||
									dialog.userToBan?.name ||
									dialog.userToBan?.id}
							</span>{" "}
							from accessing the platform.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<BrandInputGroup
							label="Reason (optional)"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="text"
								className="h-9 text-sm file:text-sm"
								placeholder="Violation of terms..."
								value={banForm.reason}
								onChange={(e) =>
									setBanForm((prev) => ({
										...prev,
										reason: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
						<BrandInputGroup
							label="Duration (days, leave empty for permanent)"
							className="[&_label]:text-sm [&_p]:text-sm"
						>
							<BrandInput
								type="number"
								className="h-9 text-sm file:text-sm"
								placeholder="30"
								min="1"
								value={banForm.expiresInDays}
								onChange={(e) =>
									setBanForm((prev) => ({
										...prev,
										expiresInDays: e.target.value,
									}))
								}
							/>
						</BrandInputGroup>
					</div>
					<DialogFooter>
						<BrandButton
							variant="secondary"
							className="h-9 text-sm"
							onClick={() => dispatch({ type: "SET_BAN", user: null })}
						>
							Cancel
						</BrandButton>
						<BrandButton
							variant="destructive"
							className="h-9 text-sm"
							onClick={handleBanUser}
							disabled={banningUserId === dialog.userToBan?.id}
						>
							{banningUserId === dialog.userToBan?.id ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : (
								<Ban className="w-4 h-4 mr-2" />
							)}
							Ban
						</BrandButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Sessions dialog */}
			<Dialog
				open={!!dialog.sessionsUser}
				onOpenChange={() => {
					dispatch({ type: "SET_SESSIONS", user: null });
					setSessions([]);
				}}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>Sessions</DialogTitle>
						<DialogDescription>
							Active sessions for{" "}
							{dialog.sessionsUser?.email ||
								dialog.sessionsUser?.name ||
								dialog.sessionsUser?.id}
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						{loadingSessions ? (
							<div className="flex items-center justify-center py-8">
								<Loader2 className="w-6 h-6 animate-spin text-primary" />
							</div>
						) : sessions.length === 0 ? (
							<div className="py-8 text-center text-sm text-muted-foreground">
								No active sessions
							</div>
						) : (
							<div className="space-y-3 max-h-80 overflow-y-auto">
								{sessions.map((s) => (
									<div
										key={s.id}
										className="flex items-center justify-between p-3 bg-muted/50"
									>
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm font-bold">
												{s.userAgent
													? s.userAgent.substring(0, 50) +
														(s.userAgent.length > 50 ? "..." : "")
													: "Unknown device"}
											</div>
											<div className="text-xs text-muted-foreground">
												{s.ipAddress || "Unknown IP"} &bull; Created{" "}
												{new Date(s.createdAt).toLocaleDateString()}
											</div>
										</div>
										<BrandButton
											size="sm"
											variant="secondary"
											className="h-9 text-sm"
											onClick={() => handleRevokeSession(s.token)}
											disabled={revokingSessionToken === s.token}
										>
											{revokingSessionToken === s.token ? (
												<Loader2 className="w-4 h-4 animate-spin" />
											) : (
												<X className="w-4 h-4" />
											)}
										</BrandButton>
									</div>
								))}
							</div>
						)}
					</div>
					<DialogFooter>
						<BrandButton
							variant="secondary"
							className="h-9 text-sm"
							onClick={() => {
								dispatch({ type: "SET_SESSIONS", user: null });
								setSessions([]);
							}}
						>
							Close
						</BrandButton>
						{sessions.length > 0 && (
							<BrandButton
								variant="destructive"
								className="h-9 text-sm"
								onClick={handleRevokeAllSessions}
								disabled={revokingAllSessions}
							>
								{revokingAllSessions ? (
									<Loader2 className="w-4 h-4 animate-spin mr-2" />
								) : null}
								Revoke All
							</BrandButton>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
